import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface RecordTarget {
  recordId: string;
  recordType: string;
  projectId?: string | null;
}

// Which register shows each record type. Only types listed here become clickable.
const PAGE_FOR_RECORD_TYPE: { [recordType: string]: string } = {
  MIR: 'mir',
  WIR: 'wir',
  SNAG: 'snag-register',
};

interface NavigationContextValue {
  pendingTarget: RecordTarget | null;
  canOpenRecord: (recordType?: string | null) => boolean;
  openRecord: (target: RecordTarget) => void;
  clearPendingTarget: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const NavigationProvider: React.FC<{
  onNavigate: (page: string) => void;
  children: React.ReactNode;
}> = ({ onNavigate, children }) => {
  const [pendingTarget, setPendingTarget] = useState<RecordTarget | null>(null);

  const canOpenRecord = useCallback(
    (recordType?: string | null) => !!recordType && recordType in PAGE_FOR_RECORD_TYPE,
    []
  );

  const openRecord = useCallback(
    (target: RecordTarget) => {
      const page = PAGE_FOR_RECORD_TYPE[target.recordType];
      if (!page) return;
      setPendingTarget(target);
      onNavigate(page);
    },
    [onNavigate]
  );

  const clearPendingTarget = useCallback(() => setPendingTarget(null), []);

  const value = useMemo(
    () => ({ pendingTarget, canOpenRecord, openRecord, clearPendingTarget }),
    [pendingTarget, canOpenRecord, openRecord, clearPendingTarget]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = (): NavigationContextValue => {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used within NavigationProvider');
  return ctx;
};